<template>
  <div class="flex gap-2">
    <div class="p-4 flex flex-col justify-between w-70 rounded-3xl bg-key">
      <div class="flex-1">
        <div class="flex items-center gap-1">
          <Icon
            icon="Technology-Wireframe-RTTriggerRT"
            class="size-5"
            :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
          />
          <span
            class="text-m-medium"
            :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
            >{{ t('itinerary.rtSensitivityTrigger.triggerSensitivity') }}</span
          >
          <Tooltip
            :content="t('itinerary.rtSensitivityTrigger.triggerTooltip')"
            side="bottom"
            align="start"
            :disabled="disabled"
          >
            <Icon
              icon="Function-Wireframe-Caution"
              class="w-4 h-4"
              :class="`${disabled ? 'text-neutral-dark-disabled' : 'text-text-glass-default'}`"
            />
          </Tooltip>
        </div>
        <div class="mt-4 flex items-center gap-0.5">
          <Button
            :disabled="disabled"
            color="text-b-w"
            variant="frameless"
            shape="square"
            size="icon-xs"
            @click="handleTriggerStepDown"
          >
            <Icon icon="Direction-Wireframe-Left2" :class="{ 'text-text-disabled': disabled }" />
          </Button>
          <div class="flex items-center gap-3" :class="{ 'opacity-25': disabled }">
            <span class="text-s-medium text-black-white">0.1</span>
            <div class="relative">
              <Slider
                v-model="triggerDisplay"
                :min="0.1"
                :max="3.3"
                :step="0.1"
                :disabled="disabled"
                thumb-alignment="overflow"
                class="h-3 w-38"
                track-class="h-3 bg-linear-[var(--color-rt2)]"
                range-class="bg-transparent"
                thumb-class="bg-transparent w-3.5 h-5 rounded-none"
                @value-commit="commitTrigger"
              >
                <template #thumb>
                  <div class="relative flex items-center w-full h-full">
                    <Icon icon="Direction-FIll-SwipeLR1" class="w-3.5 h-3.5 text-black" />
                    <div class="absolute h-5 w-px bg-black left-[50%]" />
                  </div>
                </template>
              </Slider>
              <div
                class="absolute top-3.5 left-0 -translate-x-1/2 pointer-events-none text-center leading-none"
                :style="{ left: triggerBubbleLeft }"
              >
                <div class="text-black-white text-s-medium">{{ triggerDisplay[0] }}mm</div>
              </div>
            </div>
            <span class="text-s-medium text-black-white">3.3</span>
          </div>
          <Button
            :disabled="disabled"
            color="text-b-w"
            variant="frameless"
            shape="square"
            size="icon-xs"
            @click="handleTriggerStepUp"
          >
            <Icon
              icon="Direction-Wireframe-Right2"
              :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
            />
          </Button>
        </div>
      </div>
      <div class="relative h-8 shrink-0 flex items-center mb-1">
        <div class="h-px w-full bg-divider" />
        <div class="absolute left-1/2 -translate-x-1/2">
          <Tooltip
            :content="
              isRelated
                ? t('itinerary.rtSensitivityTrigger.linkedMode')
                : t('itinerary.rtSensitivityTrigger.unlinkedMode')
            "
            side="bottom"
            align="start"
          >
            <Button
              color="glass"
              shape="circle"
              size="icon-sm"
              :disabled="disabled || !relatedToggleEnabled"
              @click="isRelated = !isRelated"
            >
              <Icon
                :icon="isRelated ? 'Function-Wireframe-Link' : 'Function-Wireframe-Unlink'"
                class="w-5.5 h-5.5"
              />
            </Button>
          </Tooltip>
        </div>
      </div>
      <div class="flex-1">
        <div class="flex items-center gap-1">
          <Icon
            icon="Technology-Wireframe-RTTriggerRT"
            class="size-5"
            :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
          />
          <span
            class="text-m-medium text-black-white"
            :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
            >{{ t('itinerary.rtSensitivityTrigger.resetSensitivity') }}</span
          >
          <Tooltip
            :content="t('itinerary.rtSensitivityTrigger.resetTooltip')"
            side="bottom"
            align="start"
            :disabled="disabled"
          >
            <Icon
              icon="Function-Wireframe-Caution"
              class="w-4 h-4"
              :class="`${disabled ? 'text-neutral-dark-disabled' : 'text-text-glass-default'}`"
            />
          </Tooltip>
        </div>
        <div class="mt-4 flex items-center gap-0.5">
          <Button
            :disabled="disabled"
            color="text-b-w"
            variant="frameless"
            shape="square"
            size="icon-xs"
            @click="handleResetStepDown"
          >
            <Icon
              icon="Direction-Wireframe-Left2"
              :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
            />
          </Button>
          <div class="flex items-center gap-3" :class="{ 'opacity-25': disabled }">
            <span class="text-s-medium text-black-white">0.1</span>
            <div class="relative">
              <Slider
                v-model="resetDisplay"
                :min="0.1"
                :max="3.3"
                :step="0.1"
                :disabled="disabled"
                thumb-alignment="overflow"
                class="h-3 w-38"
                track-class="h-3 bg-linear-[var(--color-rt2)]"
                range-class="bg-transparent"
                thumb-class="bg-transparent w-3.5 h-5 rounded-none"
                @value-commit="commitReset"
              >
                <template #thumb>
                  <div class="relative flex items-center w-full h-full">
                    <Icon icon="Direction-FIll-SwipeLR1" class="w-3.5 h-3.5 text-black" />
                    <div class="absolute h-5 w-px bg-black left-[50%]" />
                  </div>
                </template>
              </Slider>
              <div
                class="absolute top-3.5 left-0 -translate-x-1/2 pointer-events-none text-center leading-none"
                :style="{ left: resetBubbleLeft }"
              >
                <div class="text-black-white text-s-medium">{{ resetDisplay[0] }}mm</div>
              </div>
            </div>
            <span class="text-s-medium text-black-white">3.3</span>
          </div>
          <Button
            :disabled="disabled"
            color="text-b-w"
            variant="frameless"
            shape="square"
            size="icon-xs"
            @click="handleResetStepUp"
          >
            <Icon
              icon="Direction-Wireframe-Right2"
              :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
            />
          </Button>
        </div>
      </div>
    </div>
    <div class="p-4 w-35.25 flex flex-col rounded-3xl bg-key">
      <div class="flex items-center gap-1 h-5.5 text-black-white">
        <Icon icon="Function-Wireframe-Switch" class="size-5" />
        <span class="text-m-medium">
          {{ t('itinerary.rtSensitivityTrigger.switchKey') }}
        </span>
      </div>
      <div
        class="mt-2 flex-1 relative flex flex-col rounded-lg bg-neutral-light-default1 overflow-hidden"
      >
        <div
          class="absolute left-0 right-0 flex-1 h-11 rounded-lg bg-white transition-all duration-300 ease-in-out"
          :style="{ top: `${rtSwitchIndex * 44}px` }"
        />
        <button
          v-for="(item, index) in rtSwitchOptions"
          :key="item.value"
          class="relative z-1 h-11 text-m-medium rounded-lg text-center cursor-pointer transition-colors duration-300 hover:bg-neutral-light-default1"
          :class="`${rtSwitchIndex === index ? 'text-black' : 'text-black-white'}`"
          @click="emit('switch-rt', item.value as 0 | 1 | 2 | 3)"
        >
          {{ item.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { Icon, Button, Slider, Tooltip } from '@rapoo/element'
  import { useI18n } from 'vue-i18n'
  import type { RTIndex } from '../composables/useItineraryDevice'

  const isRelated = defineModel<boolean>('isRelated', { required: true })

  const props = defineProps<{
    rtTriggerSensitivity: number
    rtResetSensitivity: number
    sensitivityEnabled: boolean
    relatedToggleEnabled: boolean
    rtSwitchIndex: number
    presetLabels: string[]
  }>()

  const emit = defineEmits<{
    'update:rtTriggerSensitivity': [value: number]
    'update:rtResetSensitivity': [value: number]
    'switch-rt': [index: RTIndex]
  }>()

  const { t } = useI18n()

  const rtSwitchOptions = computed(() => [
    { label: props.presetLabels[0], value: 0 },
    { label: props.presetLabels[1], value: 1 },
    { label: props.presetLabels[2], value: 2 },
    { label: t('itinerary.rtSensitivityTrigger.custom'), value: 3 },
  ])

  const disabled = computed(() => !props.sensitivityEnabled)

  const triggerDisplay = ref([props.rtTriggerSensitivity / 100])
  watch(
    () => props.rtTriggerSensitivity,
    (val) => {
      triggerDisplay.value = [val / 100]
    },
  )

  const commitTrigger = (val: number[]) => {
    emit('update:rtTriggerSensitivity', Math.round(val[0] * 100))
  }

  const resetDisplay = ref([props.rtResetSensitivity / 100])
  watch(
    () => props.rtResetSensitivity,
    (val) => {
      resetDisplay.value = [val / 100]
    },
  )

  const commitReset = (val: number[]) => {
    emit('update:rtResetSensitivity', Math.round(val[0] * 100))
  }

  const triggerBubbleLeft = computed(() => {
    const ratio = (triggerDisplay.value[0] - 0.1) / (3.3 - 0.1)
    const progress = Math.min(1, Math.max(0, ratio))
    return `${progress * 100}%`
  })

  const resetBubbleLeft = computed(() => {
    const ratio = (resetDisplay.value[0] - 0.1) / (3.3 - 0.1)
    const progress = Math.min(1, Math.max(0, ratio))
    return `${progress * 100}%`
  })

  const handleTriggerStepUp = () => {
    if (disabled.value) return
    emit('update:rtTriggerSensitivity', Math.min(330, props.rtTriggerSensitivity + 10))
  }

  const handleTriggerStepDown = () => {
    if (disabled.value) return
    emit('update:rtTriggerSensitivity', Math.max(10, props.rtTriggerSensitivity - 10))
  }

  const handleResetStepUp = () => {
    if (disabled.value) return
    emit('update:rtResetSensitivity', Math.min(330, props.rtResetSensitivity + 10))
  }

  const handleResetStepDown = () => {
    if (disabled.value) return
    emit('update:rtResetSensitivity', Math.max(10, props.rtResetSensitivity - 10))
  }
</script>

<style scoped></style>
